import { assertNever } from '@kofno/piper';
import { useQueryClient } from '@tanstack/react-query';
import { observer } from 'mobx-react';
import * as React from 'react';
import LearningCollectionsStore from '../../../Store';
import { loadCollaboratorAccess, saveCollaboratorAccess } from './Api';
import CollaboratorAccessModal from './CollaboratorAccessModal';
import CollaboratorAccessStore from './Store';

interface Props {
  store: CollaboratorAccessStore;
  collectionId: number;
  collectionName: string;
  learningCollectionsStore: LearningCollectionsStore;
}

function CollaboratorAccess({
  store,
  collectionId,
  collectionName,
  learningCollectionsStore,
}: Props) {
  const queryClient = useQueryClient();
  const { state } = store;

  // Each transition produces a fresh state object, so this runs exactly once
  // per entry into loading / saving / saved.
  React.useEffect(() => {
    switch (state.kind) {
      case 'loading':
        loadCollaboratorAccess(state.link, store);
        break;
      case 'saving':
        saveCollaboratorAccess(state.resource, state.draft, store);
        break;
      case 'saved':
        // No confirmation step: flag the card for its "updated just now"
        // toaster, refresh the list (permission_type decides which admins see
        // the collection), and close.
        learningCollectionsStore.collaboratorAccessUpdated(collectionId);
        queryClient.invalidateQueries({ queryKey: ['learning-collections-index'] });
        store.close();
        break;
      case 'closed':
      case 'ready':
      case 'save-failed':
      case 'load-failed':
        break;
      default:
        assertNever(state);
    }
  }, [state, store, queryClient, collectionId, learningCollectionsStore]);

  switch (state.kind) {
    case 'closed':
    case 'saved':
      return <></>;
    case 'loading':
    case 'ready':
    case 'saving':
    case 'save-failed':
    case 'load-failed':
      return (
        <CollaboratorAccessModal state={state} store={store} collectionName={collectionName} />
      );
    default:
      return assertNever(state);
  }
}

export default observer(CollaboratorAccess);
